import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { apiFetch } from "../api/api";

const fmtNum = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString("en-IN");
};

const SummaryChip = ({ label, value, color = "default" }) => (
  <Chip
    color={color}
    variant={color === "default" ? "outlined" : "filled"}
    label={`${label}: ${fmtNum(value)}`}
    sx={{ fontWeight: 900 }}
  />
);

export default function AdminIndividualData() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState(null);
  const [imports, setImports] = useState([]);
  const [loadingImports, setLoadingImports] = useState(false);
  const [refreshNo, setRefreshNo] = useState(0);

  const fetchImports = async () => {
    setLoadingImports(true);
    try {
      const data = await apiFetch("/api/v1/auth/admin/etf/individual/imports?limit=8");
      setImports(data.rows || []);
    } catch (_) {
      setImports([]);
    } finally {
      setLoadingImports(false);
    }
  };

  useEffect(() => {
    fetchImports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshNo]);

  const onUpload = async () => {
    if (!file) {
      setError("Please select a zip file");
      return;
    }

    setUploading(true);
    setError("");
    setSummary(null);

    try {
      const form = new FormData();
      form.append("file", file);

      const data = await apiFetch("/api/v1/auth/admin/etf/individual/upload", {
        method: "POST",
        body: form,
      });

      setSummary(data.summary || null);
      setFile(null);
      setRefreshNo((x) => x + 1);
    } catch (err) {
      setError(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={2.4}>
        <Paper
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: 4,
            background:
              "linear-gradient(135deg, rgba(15,23,42,0.96), rgba(30,41,59,0.92))",
          }}
        >
          <Stack spacing={0.8}>
            <Typography variant="h5" sx={{ fontWeight: 950, color: "#fff" }}>
              Individual Data
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.72)" }}>
              Upload analysis zips to update AUM/expense ratio, benchmark TRI history, and
              individual ETF price history.
            </Typography>
          </Stack>
        </Paper>

        <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 4 }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 950 }}>
                Upload Individual Data Zip
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Expected zip contents: one AUM_ER.xlsx, one Historical_TR file, and one or more
                ETF history files.
              </Typography>
            </Box>

            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems="center">
              <Button variant="outlined" component="label" disabled={uploading}>
                Select ZIP
                <input
                  hidden
                  type="file"
                  accept=".zip"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
              </Button>
              <Box sx={{ flex: 1, width: "100%" }}>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {file ? file.name : "No zip selected"}
                </Typography>
              </Box>
              <Button variant="contained" onClick={onUpload} disabled={uploading}>
                {uploading ? "Uploading..." : "Upload & Import"}
              </Button>
            </Stack>

            {uploading ? <LinearProgress /> : null}
            {error ? <Alert severity="error">{error}</Alert> : null}

            {summary ? (
              <Alert severity="success">
                <Stack spacing={1.5}>
                  <Typography sx={{ fontWeight: 900 }}>
                    Imported {summary.source_file_name || "zip"} successfully.
                  </Typography>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <SummaryChip label="Files" value={summary.total_files} />
                    <SummaryChip label="AUM updated" value={summary.aum?.updated_rows} color="success" />
                    <SummaryChip label="AUM skipped" value={summary.aum?.skipped_rows} />
                    <SummaryChip
                      label="TRI inserted"
                      value={summary.benchmark?.inserted_rows}
                      color="success"
                    />
                    <SummaryChip label="TRI updated" value={summary.benchmark?.updated_rows} />
                    <SummaryChip
                      label="ETF inserted"
                      value={summary.etf?.inserted_rows}
                      color="success"
                    />
                    <SummaryChip label="ETF updated" value={summary.etf?.updated_rows} />
                    <SummaryChip label="ETF skipped" value={summary.etf?.skipped_rows} />
                  </Stack>
                  <Typography variant="body2">
                    Benchmark: {summary.benchmark?.name || "-"}
                  </Typography>
                  {summary.skipped_preview?.length ? (
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 900 }}>
                        Skipped preview
                      </Typography>
                      {summary.skipped_preview.map((item, idx) => (
                        <Typography key={`${item.type}-${idx}`} variant="body2">
                          {item.type}: {item.symbol || item.file || "-"} ({item.reason || "skipped"})
                        </Typography>
                      ))}
                    </Box>
                  ) : null}
                </Stack>
              </Alert>
            ) : null}
          </Stack>
        </Paper>

        <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 4 }}>
          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="h6" sx={{ fontWeight: 950 }}>
                Recent Individual Imports
              </Typography>
              {loadingImports ? <Chip label="Loading" size="small" /> : null}
            </Stack>
            <Divider />
            {imports.length ? (
              imports.map((row) => (
                <Box
                  key={row.import_batch_id}
                  sx={{
                    p: 1.4,
                    borderRadius: 2,
                    bgcolor: "action.hover",
                  }}
                >
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={1}
                    justifyContent="space-between"
                  >
                    <Box>
                      <Typography sx={{ fontWeight: 950 }}>
                        {row.created_at} - {row.source_file_name || "zip"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Benchmark: {row.benchmark_name || "-"}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>
                      AUM U:{fmtNum(row.aum_updated_rows)} S:{fmtNum(row.aum_skipped_rows)} |
                      TRI I:{fmtNum(row.benchmark_inserted_rows)} U:
                      {fmtNum(row.benchmark_updated_rows)} | ETF I:
                      {fmtNum(row.etf_inserted_rows)} U:{fmtNum(row.etf_updated_rows)} S:
                      {fmtNum(row.etf_skipped_rows)}
                    </Typography>
                  </Stack>
                </Box>
              ))
            ) : (
              <Typography color="text.secondary">
                No individual data imports found yet.
              </Typography>
            )}
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
}
